"use client"

import React, { useState, useMemo } from "react"
import Breadcrumb from "../../utils/breadcrumbs"
import { FiPlus, FiEye, FiTrash2, FiUsers, FiEdit3 } from "react-icons/fi"
import CreateRoleOffcanvas from "../../components/roles/create-role-offcanvas"
import EditRoleOffcanvas from "../../components/roles/edit-role-offcanvas"
import ViewRoleOffcanvas from "../../components/roles/view-role-offcanvas"
import ConfirmationModal from "../../ui/confirmation-modal"
import Pagination from "../../utils/pagination2"
import Loader, { ListLoader } from "../../utils/loader"
import toast from "react-hot-toast"
import api from "../../libs/axios"

export interface IPermission {
    id: number
    name: string
    description: string
    resource: string
    action: string
}

export interface IRole {
    id: number
    name: string
    description?: string
    permissions?: IPermission[]
    user_count?: string
    is_active: boolean
    created_at?: Date
    updated_at?: Date
}

const Roles: React.FC = () => {
    const [selectedRole, setSelectedRole] = useState<IRole | null>(null)
    const [searchParams, setSearchParams] = useState<Record<string, string>>({})
    const [showAddRoleCanvas, setShowAddRoleCanvas] = useState(false)
    const [showEditRoleCanvas, setShowEditRoleCanvas] = useState(false)
    const [showViewRoleCanvas, setShowViewRoleCanvas] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [roles, setRoles] = useState<IRole[]>([])
    const [loading, setLoading] = useState(false)

    // Pagination state variables
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    });


    // Filter roles based on search parameters
    const filteredRoles = useMemo(() => {
        return roles.filter((role) => {
            const nameMatch = !searchParams.name || role.name.toLowerCase().includes(searchParams.name.toLowerCase())

            const statusMatch = !searchParams.status ||
                (searchParams.status === "active" && role.is_active) ||
                (searchParams.status === "inactive" && !role.is_active)

            return nameMatch && statusMatch
        })
    }, [roles, searchParams])

    // Fetch roles - using dummy data
    const fetchRoles = async () => {
        try {
            setLoading(true)
            const response = await api.get(`/roles?page=${currentPage}&limit=${limit}`);
            setRoles(response.data.data);
            setPagination(response.data.pagination);
        } catch (error: any) {
            console.error("Error fetching roles:", error);
            toast.error("Failed to fetch roles");
        } finally {
            setLoading(false)
        }
    };
    // Fetch roles
    React.useEffect(() => {
        fetchRoles()
    }, [currentPage, limit])

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = { ...prev }
            if (value) {
                newParams[column] = value
            } else {
                delete newParams[column]
            }
            return newParams
        })
        setCurrentPage(1)
    }

    // Get status style
    const getStatusStyle = (isActive: boolean) => {
        return isActive
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
    }

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit)
        setCurrentPage(1)
    }

    // Handle delete
    const handleDeleteClick = (role: IRole) => {
        setSelectedRole(role)
        setDeleteConfirmOpen(true)
    }

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedRole) {
            try {
                if (selectedRole.user_count && parseInt(selectedRole.user_count) > 0) {
                    toast.error("Cannot delete role with assigned users")
                    setDeleteConfirmOpen(false)
                    setSelectedRole(null)
                    return
                }
                await api.delete(`/roles/${selectedRole.id}`)
                toast.success("Role deleted successfully!")
                fetchRoles();
            } catch (err: any) {
                toast.error(err?.response?.data?.message || "Failed to delete role")
            }
            setDeleteConfirmOpen(false)
            setSelectedRole(null)
        }
    }

    // Handle row click to view role
    const handleRowClick = (role: IRole) => {
        setSelectedRole(role)
        setShowViewRoleCanvas(true)
    }

    // Handle edit role
    const handleEditRole = (role: IRole) => {
        setSelectedRole(role)
        setShowEditRoleCanvas(true)
    }

    return (
        <div className="w-full min-h-screen bg-white p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Roles & Permissions
                </h2>
                <Breadcrumb />
            </div>
            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <button
                        onClick={() => setShowAddRoleCanvas(true)}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center sm:px-4"
                    >
                        <FiPlus className="w-5 h-5 sm:mr-0" />
                        <span className="hidden sm:block">Add Role</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Role Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Description
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Users Assigned
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Role Name"
                                        value={searchParams.name || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("name", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-4 border-r"></td>
                                <td className="px-6 py-4 border-r"></td>
                                <td className="px-6 py-4 border-r">
                                    <select
                                        value={searchParams.status || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("status", e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </td>
                                <td className="px-6 py-4 border-r"></td>
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredRoles.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-8 text-gray-500">
                                        No roles found
                                    </td>
                                </tr>
                            ) : (
                                filteredRoles.map((role) => (
                                    <tr key={role.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(role)}>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex font-semibold items-center capitalize">
                                                {role.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-r">
                                            <div className="text-sm text-gray-600 max-w-xs truncate">
                                                {role.description || "No description"}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <div className="flex items-center">
                                                <FiUsers className="w-4 h-4 mr-1 text-gray-500" />
                                                <span className="text-sm font-medium">{role.user_count || 0}</span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(role.is_active)}`}>
                                                {role.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => handleRowClick(role)}
                                                    className="text-primary-600 hover:text-primary-700"
                                                    title="View Role"
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditRole(role)}
                                                    className="text-blue-600 hover:text-blue-700"
                                                    title="Edit Role"
                                                >
                                                    <FiEdit3 className="w-5 h-5" />
                                                </button>
                                                {role.id !== 1 && <button
                                                    onClick={() => handleDeleteClick(role)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Delete Role"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                <CreateRoleOffcanvas
                    showCreateRoleCanvas={showAddRoleCanvas}
                    setShowCreateRoleCanvas={setShowAddRoleCanvas}
                    fetchRoles={fetchRoles}
                />

                {selectedRole && (
                    <EditRoleOffcanvas
                        fetchRoles={fetchRoles}
                        roleId={selectedRole.id.toString()}
                        showEditRoleCanvas={showEditRoleCanvas}
                        setShowEditRoleCanvas={setShowEditRoleCanvas}
                    />
                )}

                {selectedRole && (
                    <ViewRoleOffcanvas
                        roleId={selectedRole.id.toString()}
                        showViewRoleCanvas={showViewRoleCanvas}
                        setShowViewRoleCanvas={setShowViewRoleCanvas}
                    />
                )}

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Role"
                        description={`Are you sure you want to delete the "${selectedRole?.name}" role? This action cannot be undone.${selectedRole?.user_count && parseInt(selectedRole.user_count) > 0 ? " Note: This role has assigned users and cannot be deleted." : ""}`}
                        loading={loading}
                    />
                )}
            </div>
        </div>
    )
}

export default Roles